import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Gavel } from "lucide-react";
import { submitBid } from "@/lib/api";
import type { Bid, User } from "@/lib/types";

interface BidFormProps {
	auctionId: string;
	budget: number;
	users: User[];
	onBidSubmitted: (bid: Bid) => void;
	disabled?: boolean;
}

export function BidForm({
	auctionId,
	budget,
	users,
	onBidSubmitted,
	disabled,
}: BidFormProps) {
	const [amount, setAmount] = useState("");
	const [message, setMessage] = useState("");
	const [providerId, setProviderId] = useState("");
	const [loading, setLoading] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!amount || !message || !providerId) return;
		setLoading(true);
		try {
			const bid = await submitBid(auctionId, {
				providerId,
				amount: Number(amount),
				message,
			});
			onBidSubmitted(bid);
			setAmount("");
			setMessage("");
		} finally {
			setLoading(false);
		}
	};

	return (
		<Card>
			<CardHeader className="pb-3">
				<CardTitle className="text-sm flex items-center gap-2">
					<Gavel className="h-4 w-4" />
					Place a Bid
				</CardTitle>
			</CardHeader>
			<CardContent>
				<form onSubmit={handleSubmit} className="space-y-3">
					<div className="space-y-1.5">
						<Label className="text-xs">Bid as</Label>
						<Select value={providerId} onValueChange={setProviderId}>
							<SelectTrigger className="h-9 text-sm">
								<SelectValue placeholder="Select provider" />
							</SelectTrigger>
							<SelectContent>
								{users.map((u) => (
									<SelectItem key={u.id} value={u.id}>
										{u.name} ({u.rating} ★)
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div className="space-y-1.5">
						<Label className="text-xs">
							Bid Amount{" "}
							<span className="text-muted-foreground">
								(budget: ${budget.toLocaleString()})
							</span>
						</Label>
						<Input
							type="number"
							min="1"
							placeholder="Enter amount"
							value={amount}
							onChange={(e) => setAmount(e.target.value)}
							className="h-9 text-sm"
							required
						/>
					</div>
					<div className="space-y-1.5">
						<Label className="text-xs">Proposal</Label>
						<Textarea
							placeholder="Describe your approach, experience, and timeline..."
							value={message}
							onChange={(e) => setMessage(e.target.value)}
							className="min-h-[80px] text-sm resize-none"
							required
						/>
					</div>
					<Button
						type="submit"
						className="w-full"
						size="sm"
						disabled={loading || disabled}
					>
						{loading ? "Submitting..." : "Submit Bid"}
					</Button>
				</form>
			</CardContent>
		</Card>
	);
}
